package fr.manyverse.app.infrastructure.audio

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import org.jetbrains.compose.resources.ExperimentalResourceApi

/**
 * State for the crossfade audio player.
 */
data class CrossfadePlaybackState(
    val isPlaying: Boolean = false,
    val currentSoundId: String? = null,
    val volume: Float = 1.0f,
    val isCrossfading: Boolean = false,
    val currentPositionMs: Long = 0L,
    val durationMs: Long = 0L
) {
    /**
     * Progress as a fraction from 0.0 to 1.0.
     */
    val progress: Float
        get() = if (durationMs > 0) (currentPositionMs.toFloat() / durationMs).coerceIn(
            0f,
            1f
        ) else 0f
}

/**
 * State for ambient sounds' playback.
 */
data class AmbientPlaybackState(
    val activeSounds: Set<String> = emptySet()
)

/**
 * Audio player wrapper that handles crossfade transitions between sounds.
 * Uses two internal AudioPlayer instances to achieve smooth crossfade.
 *
 * @param playerFactory Factory function to create AudioPlayer instances
 * @param scope CoroutineScope for async operations
 * @param crossfadeDurationMs Duration of crossfade in milliseconds (default 4 seconds)
 */
@OptIn(ExperimentalResourceApi::class)
class CrossfadeAudioPlayer(
    private val playerFactory: () -> AudioPlayer,
    private val scope: CoroutineScope,
    private val crossfadeDurationMs: Long = 4_000L
) {
    private var playerA: AudioPlayer = playerFactory()
    private var playerB: AudioPlayer = playerFactory()
    private var effectPlayer: AudioPlayer = playerFactory()
    private var activePlayer: AudioPlayer = playerA
    private var crossfadeJob: Job? = null
    private var progressJob: Job? = null
    private var effectJob: Job? = null

    // LRU cache for loaded audio data (LinkedHashMap preserves insertion order on all KMP targets;
    // removing then re-inserting a key moves it to the tail, marking it as most recently used).
    private val audioCache = LinkedHashMap<String, ByteArray>()

    private val _state = MutableStateFlow(CrossfadePlaybackState())
    val state: StateFlow<CrossfadePlaybackState> = _state.asStateFlow()

    private val _trackCompleted = MutableSharedFlow<String>(extraBufferCapacity = 1)
    val trackCompleted: SharedFlow<String> = _trackCompleted.asSharedFlow()

    // Ambient sounds pool
    private val ambientPlayers = mutableMapOf<String, AudioPlayer>()
    private val ambientJobs = mutableMapOf<String, Job>()
    private val ambientVolumes = mutableMapOf<String, Float>()
    private val _ambientState = MutableStateFlow(AmbientPlaybackState())
    val ambientState: StateFlow<AmbientPlaybackState> = _ambientState.asStateFlow()

    private var currentVolume: Float = 1.0f
    private var currentAudioData: ByteArray? = null
    private var currentTrackLooping: Boolean = true

    companion object {
        private const val MAX_AMBIENT_SOUNDS = 8
        private const val MAX_AUDIO_CACHE_ENTRIES = 10
    }

    init {
        startProgressTracking()
    }

    /**
     * Observes the active player's playback state and propagates progress info.
     */
    private fun startProgressTracking() {
        progressJob?.cancel()
        progressJob = scope.launch {
            // Observe active player's state and update our state with progress
            while (true) {
                val playerState = activePlayer.playbackState.value
                val currentState = _state.value

                if (currentState.isPlaying && !currentState.isCrossfading) {
                    if (!playerState.isPlaying && !currentTrackLooping && currentState.currentSoundId != null) {
                        // Track ended naturally (non-looping)
                        val completedId = currentState.currentSoundId
                        _state.value = CrossfadePlaybackState(volume = currentVolume)
                        _trackCompleted.tryEmit(completedId)
                    } else {
                        _state.value = currentState.copy(
                            currentPositionMs = playerState.currentPositionMs,
                            durationMs = playerState.durationMs
                        )
                    }
                }

                delay(250L) // Sync 4 times per second
            }
        }
    }

    /**
     * Loads audio data from Compose resources, with LRU eviction at MAX_AUDIO_CACHE_ENTRIES.
     */
    private suspend fun loadAudioData(resourcePath: String): ByteArray {
        audioCache.remove(resourcePath)?.let { cached ->
            audioCache[resourcePath] = cached
            return cached
        }
        val data = Res.readBytes(resourcePath)
        if (audioCache.size >= MAX_AUDIO_CACHE_ENTRIES) {
            val oldest = audioCache.keys.firstOrNull()
            if (oldest != null) audioCache.remove(oldest)
        }
        audioCache[resourcePath] = data
        return data
    }

    /**
     * Plays a new sound with crossfade from current sound.
     * If no sound is playing, starts immediately.
     *
     * @param resourcePath Path to the audio resource (e.g., "files/audioaudio/zero/sound.mp3")
     * @param soundId Identifier for the sound
     */
    suspend fun playWithCrossfade(resourcePath: String, soundId: String, loop: Boolean = true) {
        val currentState = _state.value

        if (currentState.currentSoundId == soundId) {
            // Same sound - just resume if paused
            if (!currentState.isPlaying) {
                activePlayer.resume()
                _state.value = currentState.copy(isPlaying = true)
            }
            return
        }

        // Load audio data
        val audioData = loadAudioData(resourcePath)
        currentAudioData = audioData

        if (currentState.isPlaying && currentState.currentSoundId != null) {
            // Crossfade to new sound
            performCrossfade(audioData, soundId, loop)
        } else {
            // No sound playing - start immediately
            currentTrackLooping = loop
            activePlayer.play(audioData, soundId, loop = loop)
            activePlayer.setVolume(currentVolume)
            _state.value = CrossfadePlaybackState(
                isPlaying = true,
                currentSoundId = soundId,
                volume = currentVolume
            )
        }
    }

    /**
     * Plays an effect sound once without affecting the current music.
     * Does not loop and does not update the current sound state.
     *
     * @param resourcePath Path to the audio resource
     * @param soundId Identifier for the sound
     * @param volume Volume level from 0.0 to 1.0
     */
    fun playEffect(resourcePath: String, soundId: String, volume: Float = 1.0f) {
        // Cancel previous effect job gracefully
        effectJob?.cancel()
        effectPlayer.stop()

        effectJob = scope.launch {
            try {
                val audioData = loadAudioData(resourcePath)
                effectPlayer.play(audioData, soundId, loop = false)
                effectPlayer.setVolume(volume.coerceIn(0f, 1f))
            } catch (_: CancellationException) {
                // Ignore cancellation - this is expected when playing a new effect quickly
            }
        }
    }

    /**
     * Plays audio from bytes directly (for user-uploaded sounds).
     *
     * @param audioData The audio data as bytes
     * @param soundId Identifier for the sound
     */
    suspend fun playFromBytes(audioData: ByteArray, soundId: String, loop: Boolean = true) {
        currentAudioData = audioData
        val currentState = _state.value

        if (currentState.currentSoundId == soundId) {
            // Same sound - just resume if paused
            if (!currentState.isPlaying) {
                activePlayer.resume()
                _state.value = currentState.copy(isPlaying = true)
            }
            return
        }

        if (currentState.isPlaying && currentState.currentSoundId != null) {
            // Crossfade to new sound
            performCrossfade(audioData, soundId, loop)
        } else {
            // No sound playing - start immediately
            currentTrackLooping = loop
            activePlayer.play(audioData, soundId, loop = loop)
            activePlayer.setVolume(currentVolume)
            _state.value = CrossfadePlaybackState(
                isPlaying = true,
                currentSoundId = soundId,
                volume = currentVolume
            )
        }
    }

    /**
     * Plays an effect sound from bytes directly (for user-uploaded sounds).
     * Does not loop and does not update the current sound state.
     *
     * @param audioData The audio data as bytes
     * @param soundId Identifier for the sound
     * @param volume Volume level from 0.0 to 1.0
     */
    fun playEffectFromBytes(audioData: ByteArray, soundId: String, volume: Float = 1.0f) {
        // Cancel previous effect job gracefully
        effectJob?.cancel()
        effectPlayer.stop()

        effectJob = scope.launch {
            try {
                effectPlayer.play(audioData, soundId, loop = false)
                effectPlayer.setVolume(volume.coerceIn(0f, 1f))
            } catch (_: CancellationException) {
                // Ignore cancellation - this is expected when playing a new effect quickly
            }
        }
    }

    private fun performCrossfade(audioData: ByteArray, soundId: String, loop: Boolean = true) {
        crossfadeJob?.cancel()

        val outgoingPlayer = activePlayer
        val incomingPlayer = if (activePlayer === playerA) playerB else playerA
        activePlayer = incomingPlayer

        _state.value = _state.value.copy(isCrossfading = true)

        crossfadeJob = scope.launch {
            try {
                // Start incoming player at zero volume
                incomingPlayer.play(audioData, soundId, loop = loop)
                incomingPlayer.setVolume(0f)

                val steps = 100
                val stepDelay = crossfadeDurationMs / steps

                for (i in 1..steps) {
                    val progress = i.toFloat() / steps
                    val fadeOutVolume = currentVolume * (1f - progress)
                    val fadeInVolume = currentVolume * progress

                    outgoingPlayer.setVolume(fadeOutVolume)
                    incomingPlayer.setVolume(fadeInVolume)

                    delay(stepDelay)
                }

                // Crossfade complete
                outgoingPlayer.stop()
                incomingPlayer.setVolume(currentVolume)
                currentTrackLooping = loop

                _state.value = CrossfadePlaybackState(
                    isPlaying = true,
                    currentSoundId = soundId,
                    volume = currentVolume,
                    isCrossfading = false
                )
            } catch (_: CancellationException) {
                // Ignore cancellation - this is expected when changing tracks quickly
            }
        }
    }

    /**
     * Restarts the currently playing track with a new loop setting, using crossfade.
     * Does nothing if no track is playing or audio data is unavailable.
     */
    suspend fun restartCurrentWithLoop(loop: Boolean) {
        val soundId = _state.value.currentSoundId ?: return
        val audioData = currentAudioData ?: return
        if (_state.value.isPlaying) {
            performCrossfade(audioData, soundId, loop)
        }
    }

    /**
     * Pauses the current playback.
     */
    fun pause() {
        crossfadeJob?.cancel()
        activePlayer.pause()
        _state.value = _state.value.copy(isPlaying = false, isCrossfading = false)
    }

    /**
     * Resumes the current playback.
     */
    fun resume() {
        activePlayer.resume()
        _state.value = _state.value.copy(isPlaying = true)
    }

    /**
     * Stops all playback.
     */
    fun stop() {
        crossfadeJob?.cancel()
        playerA.stop()
        playerB.stop()
        _state.value = CrossfadePlaybackState(volume = currentVolume)
    }

    /**
     * Stops the currently playing effect sound, if any.
     */
    fun stopEffect() {
        effectJob?.cancel()
        effectJob = null
        effectPlayer.stop()
    }

    /**
     * Sets the volume for playback.
     *
     * @param volume Volume level from 0.0 to 1.0
     */
    fun setVolume(volume: Float) {
        currentVolume = volume.coerceIn(0f, 1f)
        if (!_state.value.isCrossfading) {
            activePlayer.setVolume(currentVolume)
        }
        _state.value = _state.value.copy(volume = currentVolume)
    }

    // ==================== AMBIENT SOUNDS ====================

    /**
     * Plays an ambient sound in a loop. Multiple ambient sounds can play simultaneously.
     * If the sound is already playing, does nothing.
     *
     * @param resourcePath Path to the audio resource
     * @param soundId Identifier for the sound
     * @param volume Volume level from 0.0 to 1.0
     */
    fun playAmbient(resourcePath: String, soundId: String, volume: Float = 1.0f) {
        // Already playing, do nothing
        if (ambientPlayers.containsKey(soundId)) {
            return
        }

        // Check max limit
        if (ambientPlayers.size >= MAX_AMBIENT_SOUNDS) {
            return
        }

        ambientJobs[soundId] = scope.launch {
            try {
                val audioData = loadAudioData(resourcePath)
                val player = playerFactory()
                ambientPlayers[soundId] = player
                ambientVolumes[soundId] = volume.coerceIn(0f, 1f)

                player.play(audioData, soundId, loop = true)
                player.setVolume(volume.coerceIn(0f, 1f))

                updateAmbientState()
            } catch (_: CancellationException) {
                // Ignore cancellation
            }
        }
    }

    /**
     * Plays an ambient sound from bytes directly (for user-uploaded sounds).
     *
     * @param audioData The audio data as bytes
     * @param soundId Identifier for the sound
     * @param volume Volume level from 0.0 to 1.0
     */
    fun playAmbientFromBytes(audioData: ByteArray, soundId: String, volume: Float = 1.0f) {
        // Already playing, do nothing
        if (ambientPlayers.containsKey(soundId)) {
            return
        }

        // Check max limit
        if (ambientPlayers.size >= MAX_AMBIENT_SOUNDS) {
            return
        }

        ambientJobs[soundId] = scope.launch {
            try {
                val player = playerFactory()
                ambientPlayers[soundId] = player
                ambientVolumes[soundId] = volume.coerceIn(0f, 1f)

                player.play(audioData, soundId, loop = true)
                player.setVolume(volume.coerceIn(0f, 1f))

                updateAmbientState()
            } catch (_: CancellationException) {
                // Ignore cancellation
            }
        }
    }

    /**
     * Stops a specific ambient sound.
     *
     * @param soundId Identifier for the sound to stop
     */
    fun stopAmbient(soundId: String) {
        ambientJobs[soundId]?.cancel()
        ambientJobs.remove(soundId)

        ambientPlayers[soundId]?.let { player ->
            player.stop()
            player.release()
        }
        ambientPlayers.remove(soundId)
        ambientVolumes.remove(soundId)

        updateAmbientState()
    }

    /**
     * Stops all ambient sounds.
     */
    fun stopAllAmbients() {
        ambientJobs.values.forEach { it.cancel() }
        ambientJobs.clear()

        ambientPlayers.values.forEach { player ->
            player.stop()
            player.release()
        }
        ambientPlayers.clear()
        ambientVolumes.clear()

        updateAmbientState()
    }

    /**
     * Sets the volume for a specific ambient sound.
     *
     * @param soundId Identifier for the sound
     * @param volume Volume level from 0.0 to 1.0
     */
    fun setAmbientVolume(soundId: String, volume: Float) {
        val safeVolume = volume.coerceIn(0f, 1f)
        ambientVolumes[soundId] = safeVolume
        ambientPlayers[soundId]?.setVolume(safeVolume)
    }

    /**
     * Checks if a specific ambient sound is currently playing.
     *
     * @param soundId Identifier for the sound
     * @return true if the sound is playing, false otherwise
     */
    fun isAmbientPlaying(soundId: String): Boolean {
        return ambientPlayers.containsKey(soundId)
    }

    /**
     * Updates the ambient state flow with the current active sounds.
     */
    private fun updateAmbientState() {
        _ambientState.value = AmbientPlaybackState(
            activeSounds = ambientPlayers.keys.toSet()
        )
    }

    /**
     * Stops all playback and resets the player to a clean state for reuse.
     * Releases the underlying platform players and recreates them.
     * Use this on screen exit to free resources while keeping the singleton usable.
     */
    fun reset() {
        progressJob?.cancel()
        crossfadeJob?.cancel()
        effectJob?.cancel()
        playerA.release()
        playerB.release()
        effectPlayer.release()
        stopAllAmbients()
        playerA = playerFactory()
        playerB = playerFactory()
        effectPlayer = playerFactory()
        activePlayer = playerA
        audioCache.clear()
        currentAudioData = null
        currentTrackLooping = true
        _state.value = CrossfadePlaybackState()
        startProgressTracking()
    }

    /**
     * Releases all resources. Use on app shutdown.
     */
    fun release() {
        progressJob?.cancel()
        crossfadeJob?.cancel()
        effectJob?.cancel()
        playerA.release()
        playerB.release()
        effectPlayer.release()

        // Release all ambient players
        stopAllAmbients()
    }
}
